/**
 * SongPanel — UI for the song-level pattern arrangement tab.
 *
 * Renders the SongSequencer's block list as a horizontal lane of
 * cards. Each card shows the block's pattern name, repeat count, a
 * remove button, and a "now playing" highlight while the song is
 * running. Reorder via drag-handle. An "Add block" button at the
 * end opens a dropdown listing every saved pattern in the store.
 *
 * Wires:
 *   SongSequencer.subscribe()  → re-render block list + transport
 *   PatternStore.subscribe()    → refresh the pattern dropdown
 *
 * No async work, no virtualized lists — songs longer than ~200
 * blocks would benefit from windowing but typical usage is closer
 * to 8–32 blocks and DOM diffing the whole row each tick is cheap.
 */

import type { PatternStore, PatternData } from '@ac-slayer/synth-core';
import type { SongSequencer, SongBlock } from '../../audio/SongSequencer';

export interface SongPanelOptions {
  readonly song: SongSequencer;
  readonly patternStore: PatternStore;
}

export class SongPanel {
  readonly element: HTMLElement;
  private readonly song: SongSequencer;
  private readonly store: PatternStore;

  private readonly toolbar: HTMLElement;
  private readonly playBtn: HTMLButtonElement;
  private readonly loopBtn: HTMLButtonElement;
  private readonly clearBtn: HTMLButtonElement;
  private readonly statusEl: HTMLElement;
  private readonly lane: HTMLElement;
  private readonly emptyState: HTMLElement;

  private readonly unsubscribers: Array<() => void> = [];

  /** When the user opens the "add block" pattern picker we keep a
   *  reference so an outside-click handler can dismiss it. */
  private addPickerEl: HTMLElement | null = null;

  constructor(opts: SongPanelOptions) {
    this.song = opts.song;
    this.store = opts.patternStore;

    this.element = document.createElement('div');
    this.element.className = 'song-panel';

    // ── Toolbar ──────────────────────────────────────────────────
    this.toolbar = document.createElement('div');
    this.toolbar.className = 'song-toolbar';

    this.playBtn = document.createElement('button');
    this.playBtn.type = 'button';
    this.playBtn.className = 'btn btn-primary song-play';
    this.playBtn.addEventListener('click', () => this.song.toggle());

    this.loopBtn = document.createElement('button');
    this.loopBtn.type = 'button';
    this.loopBtn.className = 'btn btn-sm';
    this.loopBtn.title = 'Toggle loop / play once';
    this.loopBtn.addEventListener('click', () => {
      this.song.setLoopMode(this.song.getLoopMode() === 'loop' ? 'once' : 'loop');
    });

    this.clearBtn = document.createElement('button');
    this.clearBtn.type = 'button';
    this.clearBtn.className = 'btn btn-sm';
    this.clearBtn.textContent = 'Clear';
    this.clearBtn.title = 'Remove every block from the song';
    this.clearBtn.addEventListener('click', () => {
      if (this.song.getBlocks().length === 0) return;
      // Confirmation prompt — the song state is reachable across
      // sessions via localStorage so an accidental wipe would survive
      // a refresh. Keep the gate light (native confirm) rather than
      // blocking modal.
      if (!window.confirm('Remove all blocks from this song?')) return;
      this.song.clear();
    });

    this.statusEl = document.createElement('span');
    this.statusEl.className = 'song-status mono';

    this.toolbar.append(this.playBtn, this.loopBtn, this.clearBtn, this.statusEl);
    this.element.appendChild(this.toolbar);

    // ── Block lane ───────────────────────────────────────────────
    this.lane = document.createElement('div');
    this.lane.className = 'song-lane';
    this.element.appendChild(this.lane);

    this.emptyState = document.createElement('div');
    this.emptyState.className = 'song-empty';
    this.emptyState.textContent =
      'No blocks yet. Save a pattern from the Seq tab, then click + here to chain it into a song.';

    // ── Subscriptions + initial render ───────────────────────────
    this.unsubscribers.push(this.song.subscribe(() => this.render()));
    this.unsubscribers.push(this.store.subscribe(() => this.render()));
    this.render();
  }

  dispose(): void {
    for (const u of this.unsubscribers) u();
    this.unsubscribers.length = 0;
    this.element.remove();
  }

  // ── Render pipeline ──────────────────────────────────────────────

  private render(): void {
    this.renderToolbar();
    this.renderLane();
  }

  private renderToolbar(): void {
    const playing = this.song.playing();
    const blocksEmpty = this.song.getBlocks().length === 0;
    this.playBtn.textContent = playing ? '■ Stop' : '▶ Play song';
    this.playBtn.classList.toggle('playing', playing);
    this.playBtn.disabled = blocksEmpty;
    const loop = this.song.getLoopMode();
    this.loopBtn.textContent = loop === 'loop' ? 'Loop: on' : 'Loop: once';
    this.loopBtn.classList.toggle('active', loop === 'loop');
    this.clearBtn.disabled = blocksEmpty;

    const cur = this.song.getCurrentBlockIndex();
    const remaining = this.song.getCurrentRepeatsRemaining();
    if (playing && cur >= 0) {
      const block = this.song.getBlocks()[cur];
      const total = block?.repeats ?? 0;
      const playedSoFar = total - remaining + 1; // 1-indexed for display
      this.statusEl.textContent = `Block ${cur + 1}/${this.song.getBlocks().length}  ·  pass ${playedSoFar}/${total}`;
    } else if (blocksEmpty) {
      this.statusEl.textContent = '';
    } else {
      this.statusEl.textContent = `${this.song.getBlocks().length} block${this.song.getBlocks().length === 1 ? '' : 's'}`;
    }
  }

  private renderLane(): void {
    const blocks = this.song.getBlocks();
    this.lane.innerHTML = '';

    if (blocks.length === 0) {
      this.lane.appendChild(this.emptyState);
      this.lane.appendChild(this.makeAddBlockButton());
      return;
    }

    const cur = this.song.getCurrentBlockIndex();
    blocks.forEach((block, index) => {
      this.lane.appendChild(this.makeBlockCard(block, index, index === cur));
      // Connector between cards — a small chevron so the eye reads
      // "this then this then this" instead of stacking ambiguously.
      if (index < blocks.length - 1) {
        const arrow = document.createElement('span');
        arrow.className = 'song-arrow';
        arrow.textContent = '›';
        this.lane.appendChild(arrow);
      }
    });

    // Add-block button always trails the lane.
    this.lane.appendChild(this.makeAddBlockButton());
  }

  private makeBlockCard(block: SongBlock, index: number, isCurrent: boolean): HTMLElement {
    const pattern = this.store.get(block.patternId);
    const card = document.createElement('div');
    card.className = 'song-block';
    if (isCurrent) card.classList.add('playing');
    card.draggable = true;
    card.dataset['blockId'] = block.id;

    // Drag-to-reorder. We move blocks based on the source index of
    // the drag-start and the target index of the drop.
    card.addEventListener('dragstart', (e) => {
      e.dataTransfer?.setData('text/plain', String(index));
      card.classList.add('dragging');
    });
    card.addEventListener('dragend', () => card.classList.remove('dragging'));
    card.addEventListener('dragover', (e) => {
      e.preventDefault();
      card.classList.add('drop-target');
    });
    card.addEventListener('dragleave', () => card.classList.remove('drop-target'));
    card.addEventListener('drop', (e) => {
      e.preventDefault();
      card.classList.remove('drop-target');
      const fromStr = e.dataTransfer?.getData('text/plain') ?? '';
      const from = Number.parseInt(fromStr, 10);
      if (Number.isFinite(from)) this.song.moveBlock(from, index);
    });

    // Header: pattern name + remove
    const header = document.createElement('div');
    header.className = 'song-block-header';

    const nameEl = document.createElement('div');
    nameEl.className = 'song-block-name';
    if (pattern) {
      nameEl.textContent = pattern.name || '(unnamed)';
      nameEl.title = pattern.name || '';
    } else {
      nameEl.textContent = '⚠ missing pattern';
      nameEl.title = `Pattern ${block.patternId} no longer exists`;
      card.classList.add('orphan');
    }

    const removeBtn = document.createElement('button');
    removeBtn.type = 'button';
    removeBtn.className = 'song-block-remove';
    removeBtn.textContent = '×';
    removeBtn.title = 'Remove this block';
    removeBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.song.removeBlock(block.id);
    });

    header.append(nameEl, removeBtn);

    // Body: meta line — number of steps + repeats input
    const body = document.createElement('div');
    body.className = 'song-block-body';

    const stepsEl = document.createElement('span');
    stepsEl.className = 'song-block-meta';
    if (pattern) {
      stepsEl.textContent = `${pattern.length} step${pattern.length === 1 ? '' : 's'}`;
    } else {
      stepsEl.textContent = '—';
    }

    // Repeats: native number input. `min/max/step` keep input sensible;
    // we re-clamp on change anyway so paste-with-bad-value is safe.
    const repeatsLabel = document.createElement('label');
    repeatsLabel.className = 'song-block-repeats';
    repeatsLabel.title = 'How many times the pattern plays before the song advances';
    repeatsLabel.innerHTML = '<span>×</span>';
    const repeatsInput = document.createElement('input');
    repeatsInput.type = 'number';
    repeatsInput.min = '1';
    repeatsInput.max = '32';
    repeatsInput.step = '1';
    repeatsInput.value = String(block.repeats);
    repeatsInput.addEventListener('change', () => {
      const n = Number.parseInt(repeatsInput.value, 10);
      this.song.updateBlock(block.id, { repeats: Number.isFinite(n) ? n : 1 });
    });
    // Clicking the input shouldn't initiate a drag.
    repeatsInput.addEventListener('mousedown', (e) => e.stopPropagation());
    repeatsLabel.appendChild(repeatsInput);

    body.append(stepsEl, repeatsLabel);

    card.append(header, body);

    // Index number — small badge in a top corner so users can
    // reference "block 3" verbally.
    const indexBadge = document.createElement('span');
    indexBadge.className = 'song-block-index';
    indexBadge.textContent = String(index + 1);
    card.appendChild(indexBadge);

    return card;
  }

  private makeAddBlockButton(): HTMLElement {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'song-block-add';
    btn.title = 'Append a pattern block to the song';
    btn.innerHTML = '<span class="plus">+</span><span>Add block</span>';
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.openAddPicker(btn);
    });
    return btn;
  }

  private openAddPicker(anchor: HTMLElement): void {
    this.closeAddPicker();
    const patterns = this.store.list();
    if (patterns.length === 0) {
      // Tiny inline notice rather than a popover — there's nothing
      // to pick yet, so show why. Same fixed-positioning trick as
      // the popover below: anchored to the trigger button via the
      // viewport so the dock's overflow doesn't clip it.
      const notice = document.createElement('div');
      notice.className = 'song-add-notice';
      notice.textContent =
        'No saved patterns. Open the Seq tab, edit a pattern, and click "Save pattern" to capture it.';
      const noticeAnchor = anchor.getBoundingClientRect();
      notice.style.position = 'fixed';
      notice.style.top = `${noticeAnchor.bottom + 6}px`;
      notice.style.left = `${Math.max(8, noticeAnchor.left)}px`;
      notice.style.maxWidth = '320px';
      this.addPickerEl = notice;
      document.body.appendChild(notice);
      // Auto-dismiss in a few seconds — informational only.
      setTimeout(() => this.closeAddPicker(), 3500);
      return;
    }

    // Group by category for tidiness when the user has built up a
    // bank of patterns. A flat list works fine for ≤10 patterns;
    // beyond that the optgroup-style layout reads better.
    const popover = document.createElement('div');
    popover.className = 'song-add-popover';
    const grouped = new Map<string, PatternData[]>();
    for (const p of patterns) {
      const cat = p.category || 'Uncategorized';
      const list = grouped.get(cat) ?? [];
      list.push(p);
      grouped.set(cat, list);
    }
    const sortedCategories = [...grouped.keys()].sort((a, b) => a.localeCompare(b));
    for (const cat of sortedCategories) {
      const group = document.createElement('div');
      group.className = 'song-add-group';
      const header = document.createElement('div');
      header.className = 'song-add-group-header';
      header.textContent = cat;
      group.appendChild(header);
      const groupPatterns = (grouped.get(cat) ?? []).slice().sort((a, b) => a.name.localeCompare(b.name));
      for (const pattern of groupPatterns) {
        const item = document.createElement('button');
        item.type = 'button';
        item.className = 'song-add-item';
        item.innerHTML = `<span class="song-add-item-name"></span><span class="song-add-item-meta mono">${pattern.length} st</span>`;
        const nameSpan = item.querySelector<HTMLElement>('.song-add-item-name');
        if (nameSpan) nameSpan.textContent = pattern.name || '(unnamed)';
        item.addEventListener('click', () => {
          this.song.addBlock(pattern.id, 1);
          this.closeAddPicker();
        });
        group.appendChild(item);
      }
      popover.appendChild(group);
    }

    // Position the popover relative to the viewport (position: fixed)
    // and mount it on document.body so it escapes the keyboard dock's
    // overflow clipping. Without this, the popover renders inside the
    // dock's slot and gets cut off vertically — invisible to the user.
    //
    // We also flip the popover above the anchor when the natural
    // below-anchor placement would run off the bottom edge of the
    // viewport. The anchor button sits in a horizontal lane and the
    // popover is taller than wide, so this matters in practice.
    const rect = anchor.getBoundingClientRect();
    const POPOVER_GAP = 6;
    const ESTIMATED_MAX_HEIGHT = 260; // matches CSS max-height
    const wouldOverflowBottom =
      rect.bottom + POPOVER_GAP + ESTIMATED_MAX_HEIGHT > window.innerHeight;
    popover.style.position = 'fixed';
    if (wouldOverflowBottom) {
      // Anchor by `bottom` so the popover grows upward.
      popover.style.bottom = `${window.innerHeight - rect.top + POPOVER_GAP}px`;
    } else {
      popover.style.top = `${rect.bottom + POPOVER_GAP}px`;
    }
    popover.style.left = `${Math.max(8, rect.left)}px`;

    this.addPickerEl = popover;
    document.body.appendChild(popover);

    // Outside-click dismissal. Defer the listener attachment one
    // microtask so the click that opened the popover doesn't
    // immediately close it.
    queueMicrotask(() => {
      const onOutside = (e: MouseEvent): void => {
        if (!popover.contains(e.target as Node)) {
          this.closeAddPicker();
          document.removeEventListener('mousedown', onOutside);
        }
      };
      document.addEventListener('mousedown', onOutside);
    });
  }

  private closeAddPicker(): void {
    this.addPickerEl?.remove();
    this.addPickerEl = null;
  }
}
